import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TimeSeriesData, TimeFrame } from '@/types/dashboard';

interface TimeSeriesChartProps {
    data: TimeSeriesData[];
    timeFrame: TimeFrame;
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ data, timeFrame }) => {

    const formatXAxisLabel = (timestamp: string) => {
        const date = new Date(timestamp);
        if (timeFrame === '1h') {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (timeFrame === '24h') {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const formatTooltipValue = (value: number | string | Array<number | string> | undefined, name: string | undefined) => {
        if (value === undefined) return ['', name];
        const numValue = Number(value);
        if (isNaN(numValue)) return [String(value), name];

        if (name?.includes('Tokens')) {
            return [`${numValue.toLocaleString()} tokens`, name];
        }
        if (name?.includes('Timing')) {
            return [`${numValue.toFixed(2)}s`, name];
        }
        return [numValue.toLocaleString(), name];
    };

    const chartData = data.map(item => ({
        ...item,
        timestamp: formatXAxisLabel(item.timestamp),
    }));

    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis
                        dataKey="timestamp"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                    />
                    <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: "hsl(var(--background))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: '8px',
                        }}
                        labelStyle={{ color: "hsl(var(--foreground))" }}
                        formatter={formatTooltipValue}
                    />
                    <Legend
                        wrapperStyle={{ color: "hsl(var(--muted-foreground))" }}
                    />
                    <Line
                        type="monotone"
                        dataKey="activeUsers"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        name="Active Users"
                        dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="threads"
                        stroke="hsl(var(--destructive))"
                        strokeWidth={2}
                        name="Threads"
                        dot={{ fill: "hsl(var(--destructive))", strokeWidth: 2, r: 4 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="messages"
                        stroke="hsl(var(--ring))"
                        strokeWidth={2}
                        name="Messages"
                        dot={{ fill: "hsl(var(--ring))", strokeWidth: 2, r: 4 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="inputTokens"
                        stroke="hsl(var(--foreground))"
                        strokeWidth={2}
                        name="Input Tokens"
                        dot={{ fill: "hsl(var(--foreground))", strokeWidth: 2, r: 4 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="outputTokens"
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth={2}
                        name="Output Tokens"
                        dot={{ fill: "hsl(var(--muted-foreground))", strokeWidth: 2, r: 4 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default TimeSeriesChart;
